import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'semantic-ui-react';
import api from '../../../../../api';
import { formatPhoneNumber, validatePhoneNumber, getPhoneErrorMessage } from '../../utils/phoneValidation';
import './SmsModals.css';

const A2PBrandModal = ({ userId, brandData, isEditMode, isApproved = false, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [brandType, setBrandType] = useState('sole_proprietor'); // 'sole_proprietor' or 'standard'
  const errorRef = useRef(null);
  const modalContentRef = useRef(null);

  const initialFormState = {
    // Common fields
    street: '',
    street_secondary: '',
    city: '',
    region: '',
    postal_code: '',
    iso_country: 'US',
    vertical: '',

    // Sole Proprietor specific
    brand_name: '',
    first_name: '',
    last_name: '',
    mobile_phone_number: '',
    email: '',
    customer_name: '',

    // Standard Business specific
    business_name: '',
    business_type: '',
    business_industry: '',
    business_registration_identifier: 'EIN',
    business_registration_number: '',
    business_identity: '',
    business_regions_of_operation: 'USA_AND_CANADA',
    rep_first_name: '',
    rep_last_name: '',
    business_title: '',
    job_position: '',
    phone_number: '',
    brand_contact_email: '',
    website_url: '',
    social_media_profile_urls: '',
    stock_exchange: '',
    stock_ticker: '',
    company_type: '',
    skip_automatic_sec_vet: false,
  };

  const [form, setForm] = useState(initialFormState);

  const [errors, setErrors] = useState({});

  // Business types for standard registration
  const businessTypes = [
    { key: 'cooperative', text: 'Co-operative', value: 'Co-operative' },
    { key: 'corporation', text: 'Corporation', value: 'Corporation' },
    { key: 'llc', text: 'Limited Liability Corporation', value: 'Limited Liability Corporation' },
    { key: 'nonprofit', text: 'Non-profit Corporation', value: 'Non-profit Corporation' },
    { key: 'partnership', text: 'Partnership', value: 'Partnership' },
  ];

  // Business industries
  const businessIndustries = [
    { key: 'automotive', text: 'Automotive', value: 'AUTOMOTIVE' },
    { key: 'agriculture', text: 'Agriculture', value: 'AGRICULTURE' },
    { key: 'banking', text: 'Banking', value: 'BANKING' },
    { key: 'construction', text: 'Construction', value: 'CONSTRUCTION' },
    { key: 'consumer', text: 'Consumer', value: 'CONSUMER' },
    { key: 'education', text: 'Education', value: 'EDUCATION' },
    { key: 'engineering', text: 'Engineering', value: 'ENGINEERING' },
    { key: 'energy', text: 'Energy', value: 'ENERGY' },
    { key: 'oil_gas', text: 'Oil and Gas', value: 'OIL_AND_GAS' },
    { key: 'fmcg', text: 'Fast Moving Consumer Goods', value: 'FAST_MOVING_CONSUMER_GOODS' },
    { key: 'financial', text: 'Financial', value: 'FINANCIAL' },
    { key: 'fintech', text: 'Fintech', value: 'FINTECH' },
    { key: 'food_beverage', text: 'Food and Beverage', value: 'FOOD_AND_BEVERAGE' },
    { key: 'government', text: 'Government', value: 'GOVERNMENT' },
    { key: 'healthcare', text: 'Healthcare', value: 'HEALTHCARE' },
    { key: 'hospitality', text: 'Hospitality', value: 'HOSPITALITY' },
    { key: 'insurance', text: 'Insurance', value: 'INSURANCE' },
    { key: 'legal', text: 'Legal', value: 'LEGAL' },
    { key: 'manufacturing', text: 'Manufacturing', value: 'MANUFACTURING' },
    { key: 'media', text: 'Media', value: 'MEDIA' },
    { key: 'not_for_profit', text: 'Not For Profit', value: 'NOT_FOR_PROFIT' },
    { key: 'online', text: 'Online', value: 'ONLINE' },
    { key: 'professional_services', text: 'Professional Services', value: 'PROFESSIONAL_SERVICES' },
    { key: 'raw_materials', text: 'Raw Materials', value: 'RAW_MATERIALS' },
    { key: 'real_estate', text: 'Real Estate', value: 'REAL_ESTATE' },
    { key: 'religion', text: 'Religion', value: 'RELIGION' },
    { key: 'retail', text: 'Retail', value: 'RETAIL' },
    { key: 'jewelry', text: 'Jewelry', value: 'JEWELRY' },
    { key: 'technology', text: 'Technology', value: 'TECHNOLOGY' },
    { key: 'telecommunications', text: 'Telecommunications', value: 'TELECOMMUNICATIONS' },
    { key: 'transportation', text: 'Transportation', value: 'TRANSPORTATION' },
    { key: 'travel', text: 'Travel', value: 'TRAVEL' },
  ];

  // Registration identifier types
  const registrationIdentifiers = [
    { key: 'ein', text: 'EIN (Employer Identification Number)', value: 'EIN' },
    { key: 'duns', text: 'DUNS (Data Universal Numbering System)', value: 'DUNS' },
    { key: 'cbn', text: 'CBN (Canadian Business Number)', value: 'CBN' },
    { key: 'cn', text: 'CN (Company Number)', value: 'CN' },
    { key: 'acn', text: 'ACN (Australian Company Number)', value: 'ACN' },
    { key: 'cin', text: 'CIN (Corporate Identity Number)', value: 'CIN' },
    { key: 'vat', text: 'VAT (Value Added Tax)', value: 'VAT' },
    { key: 'vatrn', text: 'VATRN (VAT Registration Number)', value: 'VATRN' },
    { key: 'rn', text: 'RN (Registration Number)', value: 'RN' },
    { key: 'other', text: 'Other', value: 'Other' },
  ];

  // Job positions (Twilio allowed: Director, GM, VP, CEO, CFO, General Counsel, Other)
  const jobPositions = [
    { key: 'director', text: 'Director', value: 'Director' },
    { key: 'gm', text: 'GM', value: 'GM' },
    { key: 'vp', text: 'VP', value: 'VP' },
    { key: 'ceo', text: 'CEO', value: 'CEO' },
    { key: 'cfo', text: 'CFO', value: 'CFO' },
    { key: 'general_counsel', text: 'General Counsel', value: 'General Counsel' },
    { key: 'other', text: 'Other', value: 'Other' },
  ];

  // Business identity (Twilio allowed: direct_customer, isv_reseller_or_partner, unknown)
  const businessIdentities = [
    { key: 'direct_customer', text: 'Direct Customer', value: 'direct_customer' },
    { key: 'isv_reseller_or_partner', text: 'ISV/Reseller or Partner', value: 'isv_reseller_or_partner' },
    { key: 'unknown', text: 'Unknown', value: 'unknown' },
  ];

  // Company types (Twilio-required casing)
  const companyTypeOptions = [
    { key: 'private',    text: 'Private',    value: 'Private' },
    { key: 'public',     text: 'Public',     value: 'Public' },
    { key: 'nonprofit',  text: 'Nonprofit',  value: 'Nonprofit' },
    { key: 'government', text: 'Government', value: 'Government' },
  ];

  // US States
  const usStates = [
    { key: 'AL', text: 'AL', value: 'AL' }, { key: 'AK', text: 'AK', value: 'AK' },
    { key: 'AZ', text: 'AZ', value: 'AZ' }, { key: 'AR', text: 'AR', value: 'AR' },
    { key: 'CA', text: 'CA', value: 'CA' }, { key: 'CO', text: 'CO', value: 'CO' },
    { key: 'CT', text: 'CT', value: 'CT' }, { key: 'DE', text: 'DE', value: 'DE' },
    { key: 'FL', text: 'FL', value: 'FL' }, { key: 'GA', text: 'GA', value: 'GA' },
    { key: 'HI', text: 'HI', value: 'HI' }, { key: 'ID', text: 'ID', value: 'ID' },
    { key: 'IL', text: 'IL', value: 'IL' }, { key: 'IN', text: 'IN', value: 'IN' },
    { key: 'IA', text: 'IA', value: 'IA' }, { key: 'KS', text: 'KS', value: 'KS' },
    { key: 'KY', text: 'KY', value: 'KY' }, { key: 'LA', text: 'LA', value: 'LA' },
    { key: 'ME', text: 'ME', value: 'ME' }, { key: 'MD', text: 'MD', value: 'MD' },
    { key: 'MA', text: 'MA', value: 'MA' }, { key: 'MI', text: 'MI', value: 'MI' },
    { key: 'MN', text: 'MN', value: 'MN' }, { key: 'MS', text: 'MS', value: 'MS' },
    { key: 'MO', text: 'MO', value: 'MO' }, { key: 'MT', text: 'MT', value: 'MT' },
    { key: 'NE', text: 'NE', value: 'NE' }, { key: 'NV', text: 'NV', value: 'NV' },
    { key: 'NH', text: 'NH', value: 'NH' }, { key: 'NJ', text: 'NJ', value: 'NJ' },
    { key: 'NM', text: 'NM', value: 'NM' }, { key: 'NY', text: 'NY', value: 'NY' },
    { key: 'NC', text: 'NC', value: 'NC' }, { key: 'ND', text: 'ND', value: 'ND' },
    { key: 'OH', text: 'OH', value: 'OH' }, { key: 'OK', text: 'OK', value: 'OK' },
    { key: 'OR', text: 'OR', value: 'OR' }, { key: 'PA', text: 'PA', value: 'PA' },
    { key: 'RI', text: 'RI', value: 'RI' }, { key: 'SC', text: 'SC', value: 'SC' },
    { key: 'SD', text: 'SD', value: 'SD' }, { key: 'TN', text: 'TN', value: 'TN' },
    { key: 'TX', text: 'TX', value: 'TX' }, { key: 'UT', text: 'UT', value: 'UT' },
    { key: 'VT', text: 'VT', value: 'VT' }, { key: 'VA', text: 'VA', value: 'VA' },
    { key: 'WA', text: 'WA', value: 'WA' }, { key: 'WV', text: 'WV', value: 'WV' },
    { key: 'WI', text: 'WI', value: 'WI' }, { key: 'WY', text: 'WY', value: 'WY' },
  ];

  // Business Regions of Operation
  const businessRegionsOptions = [
    { key: 'USA_ALL', value: 'USA_ALL', text: 'United States (All States)' },
    { key: 'CANADA_ALL', value: 'CANADA_ALL', text: 'Canada (All Provinces)' },
    { key: 'divider1', value: 'divider1', text: '────────── USA States ──────────', disabled: true },
    { key: 'AL', value: 'AL', text: 'Alabama' },
    { key: 'AK', value: 'AK', text: 'Alaska' },
    { key: 'AZ', value: 'AZ', text: 'Arizona' },
    { key: 'AR', value: 'AR', text: 'Arkansas' },
    { key: 'CA', value: 'CA', text: 'California' },
    { key: 'CO', value: 'CO', text: 'Colorado' },
    { key: 'CT', value: 'CT', text: 'Connecticut' },
    { key: 'DE', value: 'DE', text: 'Delaware' },
    { key: 'FL', value: 'FL', text: 'Florida' },
    { key: 'GA', value: 'GA', text: 'Georgia' },
    { key: 'HI', value: 'HI', text: 'Hawaii' },
    { key: 'ID', value: 'ID', text: 'Idaho' },
    { key: 'IL', value: 'IL', text: 'Illinois' },
    { key: 'IN', value: 'IN', text: 'Indiana' },
    { key: 'IA', value: 'IA', text: 'Iowa' },
    { key: 'KS', value: 'KS', text: 'Kansas' },
    { key: 'KY', value: 'KY', text: 'Kentucky' },
    { key: 'LA', value: 'LA', text: 'Louisiana' },
    { key: 'ME', value: 'ME', text: 'Maine' },
    { key: 'MD', value: 'MD', text: 'Maryland' },
    { key: 'MA', value: 'MA', text: 'Massachusetts' },
    { key: 'MI', value: 'MI', text: 'Michigan' },
    { key: 'MN', value: 'MN', text: 'Minnesota' },
    { key: 'MS', value: 'MS', text: 'Mississippi' },
    { key: 'MO', value: 'MO', text: 'Missouri' },
    { key: 'MT', value: 'MT', text: 'Montana' },
    { key: 'NE', value: 'NE', text: 'Nebraska' },
    { key: 'NV', value: 'NV', text: 'Nevada' },
    { key: 'NH', value: 'NH', text: 'New Hampshire' },
    { key: 'NJ', value: 'NJ', text: 'New Jersey' },
    { key: 'NM', value: 'NM', text: 'New Mexico' },
    { key: 'NY', value: 'NY', text: 'New York' },
    { key: 'NC', value: 'NC', text: 'North Carolina' },
    { key: 'ND', value: 'ND', text: 'North Dakota' },
    { key: 'OH', value: 'OH', text: 'Ohio' },
    { key: 'OK', value: 'OK', text: 'Oklahoma' },
    { key: 'OR', value: 'OR', text: 'Oregon' },
    { key: 'PA', value: 'PA', text: 'Pennsylvania' },
    { key: 'RI', value: 'RI', text: 'Rhode Island' },
    { key: 'SC', value: 'SC', text: 'South Carolina' },
    { key: 'SD', value: 'SD', text: 'South Dakota' },
    { key: 'TN', value: 'TN', text: 'Tennessee' },
    { key: 'TX', value: 'TX', text: 'Texas' },
    { key: 'UT', value: 'UT', text: 'Utah' },
    { key: 'VT', value: 'VT', text: 'Vermont' },
    { key: 'VA', value: 'VA', text: 'Virginia' },
    { key: 'WA', value: 'WA', text: 'Washington' },
    { key: 'WV', value: 'WV', text: 'West Virginia' },
    { key: 'WI', value: 'WI', text: 'Wisconsin' },
    { key: 'WY', value: 'WY', text: 'Wyoming' },
    { key: 'divider2', value: 'divider2', text: '────────── Canada Provinces ──────────', disabled: true },
    { key: 'AB', value: 'AB', text: 'Alberta' },
    { key: 'BC', value: 'BC', text: 'British Columbia' },
    { key: 'MB', value: 'MB', text: 'Manitoba' },
    { key: 'NB', value: 'NB', text: 'New Brunswick' },
    { key: 'NL', value: 'NL', text: 'Newfoundland and Labrador' },
    { key: 'NT', value: 'NT', text: 'Northwest Territories' },
    { key: 'NS', value: 'NS', text: 'Nova Scotia' },
    { key: 'NU', value: 'NU', text: 'Nunavut' },
    { key: 'ON', value: 'ON', text: 'Ontario' },
    { key: 'PE', value: 'PE', text: 'Prince Edward Island' },
    { key: 'QC', value: 'QC', text: 'Quebec' },
    { key: 'SK', value: 'SK', text: 'Saskatchewan' },
    { key: 'YT', value: 'YT', text: 'Yukon' },
  ];

  useEffect(() => {
    if (isEditMode && brandData) {
      console.log('A2PBrandModal - Loading brand data for edit:', brandData);

      // Convert business_regions_of_operation from string to array if needed
      const regions = brandData.business_regions_of_operation;
      let regionsArray = [];

      if (regions) {
        if (Array.isArray(regions)) {
          regionsArray = [...new Set(regions.filter(r => r && r.trim()))];
        } else if (typeof regions === 'string' && regions.trim()) {
          try {
            const parsed = JSON.parse(regions);
            if (Array.isArray(parsed)) {
              regionsArray = [...new Set(parsed.filter(r => r && r.trim()))];
            }
          } catch {
            regionsArray = [...new Set(
              regions.split(',').map(r => r.trim()).filter(r => r && !r.startsWith('divider'))
            )];
          }
        }
      }

      // Use functional update to avoid stale state
      setForm(prevForm => {
        // Normalize company_type casing to match Dropdown option values
        const companyTypeRaw = brandData.company_type || '';
        const companyTypeMap = { private: 'Private', public: 'Public', nonprofit: 'Nonprofit', government: 'Government' };
        const normalizedCompanyType = companyTypeMap[companyTypeRaw.toLowerCase()] || companyTypeRaw;

        const updatedForm = {
          ...prevForm,
          ...brandData,
          // DB stores website as 'website' column; form field is 'website_url'
          website_url: brandData.website_url || brandData.website || prevForm.website_url,
          company_type: normalizedCompanyType,
          business_regions_of_operation: 'USA_AND_CANADA',
          business_registration_identifier: 'EIN',
        };
        console.log('A2PBrandModal - Updated form with brand data:', updatedForm);
        return updatedForm;
      });

      const detectedBrandType = brandData.brand_type || 'sole_proprietor';
      console.log('A2PBrandModal - Setting brand type:', detectedBrandType);
      setBrandType(detectedBrandType);
    } else if (!isEditMode) {
      // Reset form for create mode
      console.log('A2PBrandModal - Resetting form for create mode');
      setForm(initialFormState);
      setBrandType('sole_proprietor');
    }
  }, [isEditMode, brandData]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleFieldChange = (key, value) => {
    // Apply phone number formatting for phone fields
    let formattedValue = value;
    if (key === 'mobile_phone_number' || key === 'phone_number') {
      formattedValue = formatPhoneNumber(value);
    }

    setForm(prev => ({ ...prev, [key]: formattedValue }));
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
    if (error) setError('');
  };

  const handleRegionsChange = (e, data) => {
    const validValues = [...new Set(
      data.value.filter(v => v && v.trim() && !v.startsWith('divider'))
    )];
    handleFieldChange('business_regions_of_operation', validValues);
  };

  const validate = () => {
    const e = {};

    if (brandType === 'sole_proprietor') {
      if (!form.brand_name?.trim()) e.brand_name = 'Brand name is required';
      if (!form.first_name?.trim()) e.first_name = 'First name is required';
      if (!form.last_name?.trim()) e.last_name = 'Last name is required';

      // Validate mobile phone number
      const mobilePhoneError = getPhoneErrorMessage(form.mobile_phone_number);
      if (mobilePhoneError) {
        e.mobile_phone_number = mobilePhoneError;
      }
      if (!form.email?.trim()) {
        e.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        e.email = 'Invalid email format';
      }
      if (!form.customer_name?.trim()) e.customer_name = 'Customer name is required';
    } else {
      if (!form.business_name?.trim()) e.business_name = 'Business name is required';
      if (!form.business_type) e.business_type = 'Business type is required';
      if (!form.business_industry) e.business_industry = 'Business industry is required';
      if (!form.business_registration_number?.trim()) e.business_registration_number = 'Registration number is required';
      if (!form.business_identity) e.business_identity = 'Business identity is required';
      if (!form.company_type) e.company_type = 'Company type is required';

      if (!form.rep_first_name?.trim()) e.rep_first_name = 'Representative first name is required';
      if (!form.rep_last_name?.trim()) e.rep_last_name = 'Representative last name is required';
      if (!form.business_title?.trim()) e.business_title = 'Business title is required';
      if (!form.job_position) e.job_position = 'Job position is required';

      // Validate phone number
      const phoneError = getPhoneErrorMessage(form.phone_number);
      if (phoneError) {
        e.phone_number = phoneError;
      }
      if (!form.brand_contact_email?.trim()) {
        e.brand_contact_email = 'Brand contact email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.brand_contact_email)) {
        e.brand_contact_email = 'Invalid email format';
      }
      if (!form.website_url?.trim()) {
        e.website_url = 'Website URL is required for standard business';
      } else if (!/^https?:\/\/.+/.test(form.website_url)) {
        e.website_url = 'Website must be a valid URL starting with http:// or https://';
      }
      if (form.company_type === 'Public') {
        if (!form.stock_exchange?.trim()) e.stock_exchange = 'Stock exchange is required for public companies';
        if (!form.stock_ticker?.trim()) e.stock_ticker = 'Stock ticker is required for public companies';
      }
    }

    // Common address validation
    if (!form.street?.trim()) e.street = 'Street address is required';
    if (!form.city?.trim()) e.city = 'City is required';
    if (!form.region) e.region = 'State is required';
    if (!form.postal_code?.trim()) {
      e.postal_code = 'Postal code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(form.postal_code)) {
      e.postal_code = 'Postal code must be in format XXXXX or XXXXX-XXXX';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) {
      setError('Please fill in all required fields before proceeding.');
      // Scroll to top to show validation errors
      setTimeout(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      }, 100);
      return;
    }

    setLoading(true);
    try {
      const regionsString = 'USA_AND_CANADA';

      let payload = {
        brand_type: brandType,
        user_id: parseInt(userId),
        street: form.street,
        street_secondary: form.street_secondary,
        city: form.city,
        region: form.region,
        postal_code: form.postal_code,
        iso_country: form.iso_country,
        vertical: form.vertical,
      };

      if (brandType === 'sole_proprietor') {
        payload = {
          ...payload,
          brand_name: form.brand_name,
          first_name: form.first_name,
          last_name: form.last_name,
          mobile_phone_number: form.mobile_phone_number,
          email: form.email,
          customer_name: form.customer_name,
        };
      } else {
        payload = {
          ...payload,
          business_name: form.business_name,
          business_type: form.business_type,
          business_industry: form.business_industry,
          business_registration_identifier: form.business_registration_identifier,
          business_registration_number: form.business_registration_number,
          business_identity: form.business_identity,
          business_regions_of_operation: regionsString,
          rep_first_name: form.rep_first_name,
          rep_last_name: form.rep_last_name,
          business_title: form.business_title,
          job_position: form.job_position,
          phone_number: form.phone_number,
          brand_contact_email: form.brand_contact_email,
          website_url: form.website_url,
          social_media_profile_urls: form.social_media_profile_urls,
          stock_exchange: form.stock_exchange,
          stock_ticker: form.stock_ticker,
          company_type: form.company_type,
          skip_automatic_sec_vet: form.skip_automatic_sec_vet,
        };
      }

      const res = isEditMode
        ? await api.outbound.updateA2PBrand(payload)
        : await api.outbound.createA2PBrand(payload);

      if (res?.status || res?.success) {
        onClose(true);
      } else {
        if (res?.errors && typeof res.errors === 'object') {
          const errorMessages = Object.entries(res.errors)
            .map(([field, messages]) => {
              const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const msgList = Array.isArray(messages) ? messages : [messages];
              return `${fieldName}: ${msgList.join(', ')}`;
            })
            .join('\n');
          setError(errorMessages || res?.message || 'Validation failed.');
        } else {
          setError(res?.message || 'Failed to save A2P Brand. Please try again.');
        }
        // Scroll to top to show error message
        setTimeout(() => {
          if (modalContentRef.current) {
            modalContentRef.current.scrollTop = 0;
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error saving A2P Brand:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);

      // Extract error message from API response
      const errorMessage = err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || 'Something went wrong while saving A2P Brand. Please try again.';

      setError(errorMessage);
      // Scroll to top to show error message
      setTimeout(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sms-modal-overlay">
      <div className="sms-modal-content sms-modal-large">
        <div className="sms-modal-header">
          <h3>{isEditMode ? 'Edit A2P Brand' : 'Register A2P Brand'}</h3>
          <button className="sms-modal-close" onClick={() => onClose(false)}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="sms-modal-body" ref={modalContentRef}>
            {error && (
              <div className="alert alert-danger" ref={errorRef} style={{ whiteSpace: 'pre-line' }}>
                {error}
              </div>
            )}

            <div className="info-box">
           
              <p>A2P 10DLC brand registration establishes trust with carriers and is required for SMS messaging compliance. Provide accurate business information.</p>
            </div>

            {/* Brand Type Selection */}
            <div className="form-section">
              <h4>Brand Type</h4>
              <div className="brand-type-selector">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="brandType"
                    value="sole_proprietor"
                    checked={brandType === 'sole_proprietor'}
                    onChange={(e) => setBrandType(e.target.value)}
                  />
                  <span>Sole Proprietor</span>
                  <small>For individual proprietors or unincorporated businesses</small>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="brandType"
                    value="low_volume_standard"
                    checked={brandType === 'low_volume_standard'}
                    onChange={(e) => setBrandType(e.target.value)}
                  />
                  <span>Low-Volume Standard</span>
                  <small>For businesses sending fewer than 6,000 messages/day — $4.50 one-time fee</small>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="brandType"
                    value="standard"
                    checked={brandType === 'standard'}
                    onChange={(e) => setBrandType(e.target.value)}
                  />
                  <span>Standard Business</span>
                  <small>For registered businesses, corporations, LLCs, partnerships, etc.</small>
                </label>
              </div>
            </div>

            {/* Sole Proprietor Fields */}
            {brandType === 'sole_proprietor' && (
              <>
                <div className="form-section">
                  <h4>Proprietor Information</h4>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Brand Name <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.brand_name ? 'error' : ''}`}
                        placeholder="Usually first and last name of proprietor"
                        value={form.brand_name}
                        onChange={e => handleFieldChange('brand_name', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.brand_name && <span className="error-message">{errors.brand_name}</span>}
                    </div>

                    <div className="form-group">
                      <label>First Name <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.first_name ? 'error' : ''}`}
                        placeholder="Authorized representative first name"
                        value={form.first_name}
                        onChange={e => handleFieldChange('first_name', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.first_name && <span className="error-message">{errors.first_name}</span>}
                    </div>

                    <div className="form-group">
                      <label>Last Name <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.last_name ? 'error' : ''}`}
                        placeholder="Authorized representative last name"
                        value={form.last_name}
                        onChange={e => handleFieldChange('last_name', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.last_name && <span className="error-message">{errors.last_name}</span>}
                    </div>

                    <div className="form-group">
                      <label>Mobile Phone Number <span className="required">*</span></label>
                      <input
                        type="tel"
                        className={`form-input ${errors.mobile_phone_number ? 'error' : ''}`}
                        placeholder="(123) 456-7890"
                        value={form.mobile_phone_number}
                        onChange={e => handleFieldChange('mobile_phone_number', e.target.value)}
                        disabled={loading || isApproved}
                        maxLength="14"
                      />
                      <span className="field-hint">For one-time passcode verification. US 10-digit phone number.</span>
                      {errors.mobile_phone_number && <span className="error-message">{errors.mobile_phone_number}</span>}
                    </div>

                    <div className="form-group">
                      <label>Email <span className="required">*</span></label>
                      <input
                        type="email"
                        className={`form-input ${errors.email ? 'error' : ''}`}
                        placeholder="email@example.com"
                        value={form.email}
                        onChange={e => handleFieldChange('email', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>

                    <div className="form-group full-width">
                      <label>Customer Name (for mailing) <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.customer_name ? 'error' : ''}`}
                        placeholder="Business name for mailing address"
                        value={form.customer_name}
                        onChange={e => handleFieldChange('customer_name', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.customer_name && <span className="error-message">{errors.customer_name}</span>}
                    </div>

                    <div className="form-group full-width">
                      <label>Industry Vertical (Optional)</label>
                      <Dropdown
                        placeholder="Select Industry"
                        fluid
                        selection
                        search
                        options={businessIndustries}
                        value={form.vertical}
                        onChange={(e, { value }) => handleFieldChange('vertical', value)}
                        disabled={loading || isApproved}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Standard Business Fields */}
            {(brandType === 'standard' || brandType === 'low_volume_standard') && (
              <>
                <div className="form-section">
                  <h4>Business Information</h4>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label>Legal Business Name <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.business_name ? 'error' : ''}`}
                        placeholder="Exact legal name as per tax documents"
                        value={form.business_name}
                        onChange={e => handleFieldChange('business_name', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.business_name && <span className="error-message">{errors.business_name}</span>}
                    </div>

                    <div className="form-group">
                      <label>Business Type <span className="required">*</span></label>
                      <Dropdown
                        placeholder="Select business type"
                        fluid
                        selection
                        options={businessTypes}
                        value={form.business_type}
                        onChange={(e, { value }) => handleFieldChange('business_type', value)}
                        error={!!errors.business_type}
                        disabled={loading || isApproved}
                      />
                      {errors.business_type && <span className="error-message">{errors.business_type}</span>}
                    </div>

                    <div className="form-group">
                      <label>Business Industry <span className="required">*</span></label>
                      <Dropdown
                        placeholder="Select industry"
                        fluid
                        selection
                        search
                        options={businessIndustries}
                        value={form.business_industry}
                        onChange={(e, { value }) => handleFieldChange('business_industry', value)}
                        error={!!errors.business_industry}
                        disabled={loading || isApproved}
                      />
                      {errors.business_industry && <span className="error-message">{errors.business_industry}</span>}
                      <span className="field-hint">If you're unsure, you're likely a 'consumer' business</span>
                    </div>

                    <div className="form-group">
                      <label>Business Registration ID Type <span className="required">*</span></label>
                      <select value="EIN" disabled>
                        <option value="EIN">Employer Identification Number (EIN)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Registration Number <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.business_registration_number ? 'error' : ''}`}
                        placeholder="e.g., 12-3456789 for EIN"
                        value={form.business_registration_number}
                        onChange={e => handleFieldChange('business_registration_number', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.business_registration_number && <span className="error-message">{errors.business_registration_number}</span>}
                    </div>

                    <div className="form-group">
                      <label>Business Identity <span className="required">*</span></label>
                      <Dropdown
                        placeholder="Select Identity"
                        fluid
                        selection
                        options={businessIdentities}
                        value={form.business_identity}
                        onChange={(e, { value }) => handleFieldChange('business_identity', value)}
                        error={!!errors.business_identity}
                        disabled={loading || isApproved}
                      />
                      {errors.business_identity && <span className="error-message">{errors.business_identity}</span>}
                    </div>

                    <div className="form-group">
                      <label>Company Type <span className="required">*</span></label>
                      <Dropdown
                        placeholder="Select company type"
                        fluid
                        selection
                        options={companyTypeOptions}
                        value={form.company_type}
                        onChange={(e, { value }) => handleFieldChange('company_type', value)}
                        error={!!errors.company_type}
                        disabled={loading || isApproved}
                      />
                      {errors.company_type && <span className="error-message">{errors.company_type}</span>}
                    </div>

                    {form.company_type === 'Public' && (
                      <>
                        <div className="form-group">
                          <label>Stock Exchange <span className="required">*</span></label>
                          <input
                            type="text"
                            className={`form-input ${errors.stock_exchange ? 'error' : ''}`}
                            placeholder="e.g. NASDAQ, NYSE"
                            value={form.stock_exchange}
                            onChange={e => handleFieldChange('stock_exchange', e.target.value)}
                            disabled={loading || isApproved}
                          />
                          {errors.stock_exchange && <span className="error-message">{errors.stock_exchange}</span>}
                        </div>
                        <div className="form-group">
                          <label>Stock Ticker <span className="required">*</span></label>
                          <input
                            type="text"
                            className={`form-input ${errors.stock_ticker ? 'error' : ''}`}
                            placeholder="e.g. TWLO"
                            value={form.stock_ticker}
                            onChange={e => handleFieldChange('stock_ticker', e.target.value)}
                            disabled={loading || isApproved}
                          />
                          {errors.stock_ticker && <span className="error-message">{errors.stock_ticker}</span>}
                        </div>
                      </>
                    )}

                    <div className="form-group">
                      <label>Business Regions of Operations <span className="required">*</span></label>
                      <input
                        type="text"
                        value="USA and Canada"
                        disabled
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Website URL <span className="required">*</span></label>
                      <input
                        type="url"
                        className={`form-input ${errors.website_url ? 'error' : ''}`}
                        placeholder="https://example.com"
                        value={form.website_url}
                        onChange={e => handleFieldChange('website_url', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.website_url && <span className="error-message">{errors.website_url}</span>}
                    </div>

                    <div className="form-group full-width">
                      <label>Social Media Profile URLs (Optional)</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="LinkedIn, Facebook, Twitter URLs (comma separated)"
                        value={form.social_media_profile_urls}
                        onChange={e => handleFieldChange('social_media_profile_urls', e.target.value)}
                        disabled={loading || isApproved}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Authorized Representative</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>First Name <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.rep_first_name ? 'error' : ''}`}
                        placeholder="Representative first name"
                        value={form.rep_first_name}
                        onChange={e => handleFieldChange('rep_first_name', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.rep_first_name && <span className="error-message">{errors.rep_first_name}</span>}
                    </div>

                    <div className="form-group">
                      <label>Last Name <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.rep_last_name ? 'error' : ''}`}
                        placeholder="Representative last name"
                        value={form.rep_last_name}
                        onChange={e => handleFieldChange('rep_last_name', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.rep_last_name && <span className="error-message">{errors.rep_last_name}</span>}
                    </div>

                    <div className="form-group">
                      <label>Business Title <span className="required">*</span></label>
                      <input
                        type="text"
                        className={`form-input ${errors.business_title ? 'error' : ''}`}
                        placeholder="e.g., CEO, Director"
                        value={form.business_title}
                        onChange={e => handleFieldChange('business_title', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.business_title && <span className="error-message">{errors.business_title}</span>}
                    </div>

                    <div className="form-group">
                      <label>Job Position <span className="required">*</span></label>
                      <Dropdown
                        placeholder="Select Position"
                        fluid
                        selection
                        options={jobPositions}
                        value={form.job_position}
                        onChange={(e, { value }) => handleFieldChange('job_position', value)}
                        error={!!errors.job_position}
                        disabled={loading || isApproved}
                      />
                      {errors.job_position && <span className="error-message">{errors.job_position}</span>}
                    </div>

                    <div className="form-group">
                      <label>Phone Number <span className="required">*</span></label>
                      <input
                        type="tel"
                        className={`form-input ${errors.phone_number ? 'error' : ''}`}
                        placeholder="(123) 456-7890"
                        value={form.phone_number}
                        onChange={e => handleFieldChange('phone_number', e.target.value)}
                        disabled={loading || isApproved}
                        maxLength="14"
                      />
                      <span className="field-hint">US 10-digit phone number</span>
                      {errors.phone_number && <span className="error-message">{errors.phone_number}</span>}
                    </div>

                    <div className="form-group">
                      <label>Brand Contact Email (for 2FA) <span className="required">*</span></label>
                      <input
                        type="email"
                        className={`form-input ${errors.brand_contact_email ? 'error' : ''}`}
                        placeholder="email@example.com"
                        value={form.brand_contact_email}
                        onChange={e => handleFieldChange('brand_contact_email', e.target.value)}
                        disabled={loading || isApproved}
                      />
                      {errors.brand_contact_email && <span className="error-message">{errors.brand_contact_email}</span>}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Common Address Section */}
            <div className="form-section">
              <h4>Business Address</h4>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Street Address 1 <span className="required">*</span></label>
                  <input
                    type="text"
                    className={`form-input ${errors.street ? 'error' : ''}`}
                    placeholder="Physical street address"
                    value={form.street}
                    onChange={e => handleFieldChange('street', e.target.value)}
                    disabled={loading || isApproved}
                  />
                  {errors.street && <span className="error-message">{errors.street}</span>}
                </div>

                <div className="form-group full-width">
                  <label>Address Street 2 (Optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Suite, apartment, etc."
                    value={form.street_secondary}
                    onChange={e => handleFieldChange('street_secondary', e.target.value)}
                    disabled={loading || isApproved}
                  />
                </div>

                <div className="form-group">
                  <label>City <span className="required">*</span></label>
                  <input
                    type="text"
                    className={`form-input ${errors.city ? 'error' : ''}`}
                    placeholder="City"
                    value={form.city}
                    onChange={e => handleFieldChange('city', e.target.value)}
                    disabled={loading || isApproved}
                  />
                  {errors.city && <span className="error-message">{errors.city}</span>}
                </div>

                <div className="form-group">
                  <label>State or Province <span className="required">*</span></label>
                  <Dropdown
                    placeholder="Select State"
                    fluid
                    selection
                    search
                    options={usStates}
                    value={form.region}
                    onChange={(e, { value }) => handleFieldChange('region', value)}
                    error={!!errors.region}
                    disabled={loading || isApproved}
                  />
                  {errors.region && <span className="error-message">{errors.region}</span>}
                </div>

                <div className="form-group">
                  <label>Postal Code <span className="required">*</span></label>
                  <input
                    type="text"
                    className={`form-input ${errors.postal_code ? 'error' : ''}`}
                    placeholder="Postal code"
                    value={form.postal_code}
                    onChange={e => handleFieldChange('postal_code', e.target.value)}
                    disabled={loading || isApproved}
                  />
                  {errors.postal_code && <span className="error-message">{errors.postal_code}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="sms-modal-footer">
            <button type="button" className="btn linear-gradient-cancel yr-cancelbtn mg-r" onClick={() => onClose(false)} disabled={loading}>
              {isApproved ? 'Close' : 'Cancel'}
            </button>
            {!isApproved && (
              <button type="submit" className="btn linear-gradient zy-save-btn ml-2" disabled={loading}>
                {loading && (
                  <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px' }} role="status" aria-hidden="true"></span>
                )}
                {loading ? (isEditMode ? 'Updating...' : 'Registering...') : (isEditMode ? 'Update Brand' : 'Register Brand')}
              </button>
            )}
            {isApproved && (
              <div className="approval-notice" style={{
                color: '#059669',
                fontWeight: 600,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>✅</span>
                Brand is approved and cannot be edited
              </div>
            )}
          </div>
        </form>

        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="ui active inline loader" style={{ marginBottom: '15px' }}></div>
              <p style={{ color: '#087ab6', fontSize: '14px', fontWeight: 500 }}>
                {isEditMode ? 'Updating A2P Brand...' : 'Registering A2P Brand...'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default A2PBrandModal;
